import React, { useCallback, useRef } from 'react';
import { Configurations } from '@hpi-sox/hpstellar-core';
import loadable from '@loadable/component';

import CTOColorSelector from '../cto-color-selector';
import CTOConfigurations from '../cto-configurations';
import ConfigurationBadge from '../cto-configurations/configuration-badge';
import SimilarSupplies from '../similar-supplies';
import SimilarProductsSection from './similar-products-section';
import PrinterCartridgeColor from '../similar-supplies/printer-cartridge-color';
import { getPricePerPage } from '../similar-supplies/printer-cartridge-pageyield';
import withPageComponents from '../../../page/components/with-page-components';
import useProductPrice from '../../../hooks/useProductPrice';
import useTemplateTheme from '../../../hooks/useTemplateTheme';
import { Helpers } from '../../../core/src/helpers';
import { scrollToSpecsAndConditionallyLockView } from '../../util';
import { NAV_SECTIONS } from '../constants';
const { techSpecs: specsNAV } = NAV_SECTIONS;

const HardBundleItems = loadable(() => Helpers.retryFunc(() => import('./hard-bundle-items')));

import './specs-section.less';

const getSpecsLabel = product => {
    const { product_type } = product;
    let label = undefined; //'specifications';
    if (product_type === 'BUNDLE') {
        return 'INCLUDED';
    }
    return label;
};

const OverViewLabels = ({ overViewLabels, price, withColorPicker }) => {
    if (!withColorPicker && (!overViewLabels || overViewLabels.length < 1)) {
        return null;
    }

    return (
        <div className="featured-spec-container">
            {(overViewLabels || []).map((overview, idx) => {
                const { label, value } = overview;
                if (!value) {
                    return null;
                }
                const specMeta = [];
                try {
                    const pricePerPage = label.toLowerCase() === 'page yield' ? getPricePerPage(value, price) : null;
                    if (pricePerPage && !isNaN(pricePerPage)) {
                        !isNaN(pricePerPage) && pricePerPage > 0 && specMeta.push(`~$${pricePerPage.toFixed(2)}/page`);
                    }
                } catch (e) {}
                return (
                    <span className="featured-spec" key={`${label}-${idx}`}>
                        {label && <span className={'spec-label'}>{label}</span>}
                        {value}
                        {specMeta.length > 0 && ` | ${specMeta.join(' | ')}`}
                    </span>
                );
            })}
            {withColorPicker && (
                <CTOColorSelector size="small">
                    {({ colorSector }) => {
                        return (
                            <span className="featured-spec">
                                <span className={'spec-label'}>{'Color'}</span>
                                {colorSector}
                            </span>
                        );
                    }}
                </CTOColorSelector>
            )}
        </div>
    );
};

export const KeySellingPoints = withPageComponents(
    ({ productInitial, pdpTechSpecs, keyPoints }) => {
        const { product_type } = productInitial;
        const specsLabel = getSpecsLabel(productInitial);
        const { typography } = useTemplateTheme(['typography']);
        const { primaryLinkClassName } = typography || {};
        const hasScrolledToSpecs = useRef(null);

        let bundleText;
        const keyPointsData = keyPoints.reduce((r, ksp, idx) => {
            if (product_type === 'BUNDLE' && idx === 0 && /^save.*when.*bundle/gi.test(ksp)) {
                bundleText = ksp;
            } else {
                r.push(ksp);
            }
            return r;
        }, []);
        const { technical_specifications = [], highlights = [], datasheets = [] } = pdpTechSpecs || {};
        const showSpecsLink = technical_specifications.length > 0 || highlights.length > 0 || datasheets.length > 0;
        const seeAllLinkClick = useCallback(() => {
            try {
                scrollToSpecsAndConditionallyLockView(hasScrolledToSpecs && hasScrolledToSpecs.current, {
                    onComplete: () => {
                        try {
                            hasScrolledToSpecs.current = true;
                        } catch (e) {}
                    },
                });
            } catch (e) {}
        });

        return (
            <>
                <Configurations
                    label={specsLabel}
                    tally={bundleText}
                    dividerPosition="top"
                    className={`configComp specs${product_type ? `-${product_type.toLowerCase()}` : ''}${
                        specsLabel ? `-${specsLabel.toLowerCase().replace(/\s+/gi, '-')}` : ' no-label'
                    }`}
                    content={keyPointsData}
                    configListLimit={showSpecsLink ? 1 : undefined}
                    seeAllLinkProps={{
                        href: showSpecsLink ? `#${specsNAV.hash}` : null,
                        onClick: seeAllLinkClick,
                        className: `${primaryLinkClassName} seeAllLink`,
                    }}
                />
            </>
        );
    },
    { components: ['pdpTechSpecs'] },
);

const specsSection = ({ category, productInitial, pdpCTOConfiguration, pdpShadowCTO, v2 }) => {
    const { prices } = useProductPrice([productInitial]);
    if (productInitial) {
        const { keyPoints, overViewLabels, sku, colorCTOs, discontinuedMsg, product_type } = productInitial;
        const displaySKU = (sku || '').replace(/^P_/, '');
        const isBundle = product_type === 'BUNDLE';

        const { configurations = [] } = pdpCTOConfiguration || {};
        const price = prices[sku];
        const showKeyPoints = keyPoints && keyPoints.length > 0 && configurations.length < 1 && !isBundle;
        const { shadowCTOEnabled } = pdpShadowCTO || {};
        const withColorPicker = colorCTOs && colorCTOs.length > 0;
        if (shadowCTOEnabled) {
            return null;
        }

        return (
            <div className="pdp-specs-section">
                {v2 ? (
                    <>
                        <OverViewLabels
                            overViewLabels={overViewLabels}
                            price={price}
                            withColorPicker={withColorPicker}
                        />
                        <PrinterCartridgeColor product={productInitial} dividerPosition="top" />
                        {showKeyPoints && <KeySellingPoints productInitial={productInitial} keyPoints={keyPoints} />}
                        {isBundle && <HardBundleItems productInitial={productInitial} />}
                        <CTOConfigurations />
                        <SimilarProductsSection />
                        <SimilarSupplies />
                        {discontinuedMsg && <span className="discontinuedSku">Product # {displaySKU}</span>}
                    </>
                ) : (
                    <>
                        <ConfigurationBadge />
                        <OverViewLabels overViewLabels={overViewLabels} price={price} />
                        <PrinterCartridgeColor product={productInitial} dividerPosition="top" />
                        {showKeyPoints && <KeySellingPoints productInitial={productInitial} keyPoints={keyPoints} />}
                        {isBundle && <HardBundleItems productInitial={productInitial} />}
                        <SimilarProductsSection />
                        <SimilarSupplies />
                        <CTOConfigurations />
                        <CTOColorSelector />
                    </>
                )}
            </div>
        );
    }
    return null;
};

export default withPageComponents(specsSection, {
    components: ['productInitial', 'pdpCTOConfiguration', 'pdpShadowCTO'],
});
